import React, { createContext, useContext, ReactNode, useMemo, useState, useCallback, useEffect } from 'react';
import { setCookies } from 'cookies-next';
import Router from 'next/router';
import { FONT_SCALE } from '@styles/globalStyles';
import { getItem, setItem, KEYS } from '@utils/cache';
import { API, APIRoutes, destroySession } from '@utils/api';
import routes from '@utils/routes';

export type AppContextType = {
  isAuthenticated: boolean;
  fontScale: FONT_SCALE;
  switchFontScale: () => void;
  switchTheme: () => void;
  useHighContrastTheme: boolean;
  profile: User | null;
  setUser: (user: User) => void;
  unsetUser: () => void;
  signOut: () => void;
  authenticateFromCache: () => void;
  userRole: 'employer' | 'student' | null;
};

function noop() {
  // intentional no-op default for context
}

const resolveFontScale = (value: string | null | undefined): FONT_SCALE => {
  const key = value?.toUpperCase();
  if (key && key in FONT_SCALE) {
    return FONT_SCALE[key as keyof typeof FONT_SCALE];
  }
  return FONT_SCALE.NORMAL;
};

const AppContextDefaultState: AppContextType = {
  isAuthenticated: false,
  fontScale: FONT_SCALE.NORMAL,
  switchFontScale: noop,
  switchTheme: noop,
  useHighContrastTheme: false,
  profile: null,
  setUser: noop,
  unsetUser: noop,
  signOut: noop,
  authenticateFromCache: noop,
  userRole: null,
};

export const AppContext = createContext<AppContextType>(AppContextDefaultState);

export function useAppContext() {
  return useContext(AppContext);
}

type Props = {
  children: ReactNode;
  userProfile: User | null;
  isAuthenticated: boolean;
};

export function AppContextProvider({ children, userProfile, isAuthenticated: authenticated }: Props) {
  const [fontScale, setFontScale] = useState<FONT_SCALE>(resolveFontScale(getItem(KEYS.FONT_SCALE)));
  const [useHighContrastTheme, setHighContrast] = useState<boolean>(
    JSON.parse(getItem(KEYS.HIGH_CONTRAST) ?? 'false') as boolean,
  );
  const [profile, setProfile] = useState<User | null>(userProfile);
  const [userRole, setUserRole] = useState<'employer' | 'student' | null>(userProfile?.accountType ?? null);
  const [isAuthenticated, setIsAuthenticated] = useState(authenticated);

  if (userRole) {
    setCookies(KEYS.USER_ROLE, userRole);
  }

  useEffect(() => {
    setFontScale(resolveFontScale(getItem(KEYS.FONT_SCALE)));
    setHighContrast(JSON.parse(getItem(KEYS.HIGH_CONTRAST) ?? 'false') as boolean);
  }, []);

  const switchFontScale = useCallback(() => {
    const currentScale = (getItem(KEYS.FONT_SCALE) ?? FONT_SCALE.NORMAL) as FONT_SCALE;
    let newFontScale: FONT_SCALE;
    switch (currentScale) {
      case FONT_SCALE.NORMAL:
        newFontScale = FONT_SCALE.MEDIUM;
        break;
      case FONT_SCALE.MEDIUM:
        newFontScale = FONT_SCALE.LARGE;
        break;
      default:
        newFontScale = FONT_SCALE.NORMAL;
        break;
    }

    setFontScale(newFontScale);
    setItem(KEYS.FONT_SCALE, newFontScale);
  }, []);

  // switch theme between normal & highcontrast
  const switchTheme = useCallback(() => {
    const isHighContrast = !useHighContrastTheme;
    setHighContrast(isHighContrast);
    setItem(KEYS.HIGH_CONTRAST, isHighContrast.toString());
  }, [useHighContrastTheme]);

  // handle font scaling & high contrast mode
  useEffect(() => {
    Object.values(FONT_SCALE).forEach((scale) => document.documentElement.classList.remove(`fontsize--${scale}`));
    document.documentElement.classList.add(`fontsize--${fontScale}`);

    if (useHighContrastTheme) {
      document.documentElement.classList.add('highcontrast');
    } else {
      document.documentElement.classList.remove('highcontrast');
    }
  }, [fontScale, useHighContrastTheme]);

  const setUser = (user: User) => {
    setProfile(user);
    setUserRole(user.accountType);
    setCookies(KEYS.USER_ROLE, user.accountType);
    setIsAuthenticated(true);
  };

  const unsetUser = () => {
    setIsAuthenticated(false);
    setProfile(null);
    destroySession();
  };

  const signOut = async () => {
    try {
      await API.delete(APIRoutes.auth.signout);
      await Router.replace(routes.homepage ?? '/');
      setIsAuthenticated(false);
      setProfile(null);
      destroySession();
    } catch (error) {
      console.debug(error);
    }
  };

  const authenticateFromCache = useCallback(() => {
    const cachedProfile = getItem(KEYS.USER_PROFILE);
    if (cachedProfile) {
      setIsAuthenticated(true);
      setProfile(JSON.parse(cachedProfile) as User);
    }
  }, []);

  const value = useMemo(
    () => ({
      ...AppContextDefaultState,
      fontScale,
      switchFontScale,
      switchTheme,
      profile,
      isAuthenticated,
      setUser,
      unsetUser,
      signOut,
      authenticateFromCache,
      useHighContrastTheme,
      userRole,
    }),
    [
      fontScale,
      switchFontScale,
      switchTheme,
      profile,
      isAuthenticated,
      authenticateFromCache,
      userRole,
      useHighContrastTheme,
    ],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function withAppContext<P extends object>(Component: React.ComponentType<P & { context: AppContextType }>) {
  return function WrapperComponent(props: P) {
    return <AppContext.Consumer>{(state) => <Component {...props} context={state} />}</AppContext.Consumer>;
  };
}
